import { useRef, useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Button, Input, Field, Checkbox,
  MessageBar, MessageBarBody, MessageBarTitle, Dialog, DialogSurface,
  DialogTitle, DialogBody, DialogContent, DialogActions, DialogTrigger,
} from '@fluentui/react-components';
import { ArrowUpload20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import type { Framework, ImportResult } from '../api/client';

const useStyles = makeStyles({
  drop: {
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    textAlign: 'center', cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground2,
    ':hover': { ...shorthands.borderColor(tokens.colorBrandStroke1) },
  },
  dropActive: { ...shorthands.borderColor(tokens.colorBrandStroke1), backgroundColor: tokens.colorBrandBackground2 },
  body: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  file: { fontWeight: tokens.fontWeightSemibold },
});

export interface FrameworkImportProps {
  framework: Framework;
  onImported: () => void;
}

/**
 * Uploads a licensed framework workbook from the browser.
 *
 * The endpoint existed before this did, which meant loading a framework
 * required a terminal and a hand built multipart request. A file upload is not
 * a thing a professional application should send its users to PowerShell for.
 *
 * The attribution field is required rather than convenient: CC BY-NC-ND asks
 * for credit wherever the content is used, and the value is recorded against
 * every imported row.
 */
export function FrameworkImport({ framework, onImported }: FrameworkImportProps) {
  const styles = useStyles();
  const api = useApi();
  const fileInput = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [attribution, setAttribution] = useState(framework.publisher);
  const [commercial, setCommercial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const reset = () => {
    setFile(null); setError(null); setResult(null); setBusy(false);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const r = await api.importFramework(framework.frameworkId, file, attribution.trim(), commercial);
      setResult(r);
      onImported();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { setOpen(d.open); if (!d.open) reset(); }}>
      <DialogTrigger disableButtonEnhancement>
        <Button size="small" icon={<ArrowUpload20Regular />}>
          {framework.contentLoaded ? 'Replace content' : 'Import content'}
        </Button>
      </DialogTrigger>

      <DialogSurface>
        <DialogBody>
          <DialogTitle>Import {framework.name}</DialogTitle>

          <DialogContent>
            <div className={styles.body}>
              {result ? (
                <MessageBar intent="success">
                  <MessageBarBody>
                    <MessageBarTitle>Imported</MessageBarTitle>
                    {result.controlCount} controls and {result.safeguardCount} safeguards.
                    {result.warnings.map((w, i) => <div key={i} className={styles.muted}>{w}</div>)}
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  <MessageBar intent="info">
                    <MessageBarBody>
                      Nothing from {framework.publisher} ships with this product. Supply your
                      own licensed copy. {framework.licenceCode.toUpperCase()} permits internal
                      non-commercial use with attribution; a paid deliverable or a commercial
                      product needs a licence from the publisher.
                    </MessageBarBody>
                  </MessageBar>

                  <div
                    className={`${styles.drop} ${dragging ? styles.dropActive : ''}`}
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      const dropped = e.dataTransfer.files[0];
                      if (dropped) setFile(dropped);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click(); }}
                  >
                    {file ? (
                      <>
                        <Text className={styles.file}>{file.name}</Text>
                        <div className={styles.muted}>{Math.round(file.size / 1024)} kB</div>
                      </>
                    ) : (
                      <>
                        <Text>Drop the workbook here, or click to choose</Text>
                        <div className={styles.muted}>.xlsx published by {framework.publisher}</div>
                      </>
                    )}
                  </div>

                  <input
                    ref={fileInput}
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />

                  <Field
                    label="Attribution"
                    required
                    hint="Recorded against every imported row. The licence requires credit wherever the content is used."
                  >
                    <Input value={attribution} onChange={(_, d) => setAttribution(d.value)} />
                  </Field>

                  <Checkbox
                    checked={commercial}
                    onChange={(_, d) => setCommercial(Boolean(d.checked))}
                    label="We hold a commercial licence for this content"
                  />
                  {!commercial && (
                    <Text className={styles.muted}>
                      Without a commercial licence this content must not appear in a paid
                      deliverable. The import is recorded as non-commercial.
                    </Text>
                  )}

                  {error && (
                    <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
                  )}
                </>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{result ? 'Close' : 'Cancel'}</Button>
            </DialogTrigger>
            {!result && (
              <Button
                appearance="primary"
                disabled={!file || busy || attribution.trim().length === 0}
                onClick={() => void upload()}
              >
                {busy ? 'Importing' : 'Import'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
